/**
 * @file reportConstants.js
 * @description Shared constants for the Report Configuration module.
 *              Values here are the contract with the backend — the strings
 *              stored in ReportSchedule.Recurrence / .Granularity / .TimeRange.
 */

// ── Recurrence ─────────────────────────────────────────────────────
export const RECURRENCE = {
    NONE:    "none",     // one-time run on a specific date
    DAILY:   "daily",
    WEEKLY:  "weekly",   // on DayOfWeek
    MONTHLY: "monthly",  // on DayOfMonth (0 = last day)
};

export const RECURRENCE_OPTIONS = [
    { value: RECURRENCE.DAILY,   label: "Daily",   hint: "Every day at the run time" },
    { value: RECURRENCE.WEEKLY,  label: "Weekly",  hint: "One chosen weekday" },
    { value: RECURRENCE.MONTHLY, label: "Monthly", hint: "One chosen day of the month" },
    { value: RECURRENCE.NONE,    label: "Once",    hint: "Runs a single time, then pauses itself" },
];

// ISO numbering — 1 = Monday, matching the Mon–Sun week used everywhere else.
export const DAY_OF_WEEK_OPTIONS = [
    { value: 1, label: "Mon", full: "Monday"    },
    { value: 2, label: "Tue", full: "Tuesday"   },
    { value: 3, label: "Wed", full: "Wednesday" },
    { value: 4, label: "Thu", full: "Thursday"  },
    { value: 5, label: "Fri", full: "Friday"    },
    { value: 6, label: "Sat", full: "Saturday"  },
    { value: 7, label: "Sun", full: "Sunday"    },
];

export const DAY_OF_WEEK_LABEL = Object.fromEntries(
    DAY_OF_WEEK_OPTIONS.map(d => [d.value, d.full])
);

/** 0 is stored as "last day of the month" so 31st-of-February never happens. */
export const LAST_DAY_OF_MONTH = 0;

export const DAY_OF_MONTH_OPTIONS = [
    ...Array.from({ length: 28 }, (_, i) => ({ value: i + 1, label: ordinal(i + 1) })),
    { value: LAST_DAY_OF_MONTH, label: "Last day" },
];

// ── Granularity ────────────────────────────────────────────────────
export const GRANULARITY = {
    DAY:   "day",
    WEEK:  "week",
    MONTH: "month",
};

export const GRANULARITY_OPTIONS = [
    { value: GRANULARITY.DAY,   label: "Day",   hint: "One row per day" },
    { value: GRANULARITY.WEEK,  label: "Week",  hint: "One row per week (Mon–Sun)" },
    { value: GRANULARITY.MONTH, label: "Month", hint: "One row per month" },
];

// ── Time range ─────────────────────────────────────────────────────
export const TIME_RANGE = {
    CURRENT_WEEK:   "currentweek",    // Monday → Sunday of the current week
    CURRENT_MONTH:  "currentmonth",   // 1st → run date
    CURRENT_1MONTH: "current1month",  // 1st of last month → run date
    CURRENT_2MONTH: "current2months", // 1st of the month before last → run date
    CURRENT_3MONTH: "current3months", // 1st of three months back → run date
    CURRENT_YEAR:   "currentyear",    // Jan 1 → run date
};

/**
 * "Current + last 2 months" = this month plus the two before it — three
 * calendar months, ending on the run date. Not two months total; the label
 * counts what's added to the current one.
 */
export const TIME_RANGE_OPTIONS = [
    { value: TIME_RANGE.CURRENT_WEEK,   label: "Current week",   hint: "Mon–Sun" },
    { value: TIME_RANGE.CURRENT_MONTH,  label: "Current month",  hint: "1st → run date" },
    { value: TIME_RANGE.CURRENT_1MONTH, label: "Current + 1 mo", hint: "This month + the one before it" },
    { value: TIME_RANGE.CURRENT_2MONTH, label: "Current + 2 mo", hint: "This month + the 2 before it" },
    { value: TIME_RANGE.CURRENT_3MONTH, label: "Current + 3 mo", hint: "This month + the 3 before it" },
    { value: TIME_RANGE.CURRENT_YEAR,   label: "Current year",   hint: "Jan 1 → run date" },
];

// ── Output formats ─────────────────────────────────────────────────
export const OUTPUT = {
    PDF: "pdf",   // charts / graphs
    CSV: "csv",   // raw rows
};

// ── Time zones ─────────────────────────────────────────────────────
// Keys match ClientConfiguration.ClientTimezone (Configuration → Time Settings).
export const TIME_ZONES = {
    UTC: { key: "UTC", name: "Coordinated Universal Time", iana: "UTC" },
    PT:  { key: "PT",  name: "Pacific Time",   iana: "America/Los_Angeles" },
    MT:  { key: "MT",  name: "Mountain Time",  iana: "America/Denver" },
    CT:  { key: "CT",  name: "Central Time",   iana: "America/Chicago" },
    ET:  { key: "ET",  name: "Eastern Time",   iana: "America/New_York" },
    AKT: { key: "AKT", name: "Alaska Time",    iana: "America/Anchorage" },
    HST: { key: "HST", name: "Hawaii Time",    iana: "Pacific/Honolulu" },
};

export const DEFAULT_TIME_ZONE_KEY = "UTC";

/**
 * Values that should resolve to one of our keys but aren't spelled that way.
 * Configuration → Time Settings writes "PT", but older rows, hand-edited
 * values, and anything copied from another system show up as "PST",
 * "America/Los_Angeles", or "Pacific Time" — all of which used to fall
 * through to UTC without a word.
 *
 * MST maps to MT, which is wrong for Arizona (no DST). Nobody can express
 * Arizona in this dropdown anyway; if that ever matters, it needs its own key.
 */
export const ZONE_ALIASES = {
    PST: "PT",  PDT: "PT",  "AMERICA/LOS_ANGELES": "PT",  "PACIFIC TIME": "PT",  PACIFIC: "PT",
    MST: "MT",  MDT: "MT",  "AMERICA/DENVER": "MT",       "MOUNTAIN TIME": "MT", MOUNTAIN: "MT",
    CST: "CT",  CDT: "CT",  "AMERICA/CHICAGO": "CT",      "CENTRAL TIME": "CT",  CENTRAL: "CT",
    EST: "ET",  EDT: "ET",  "AMERICA/NEW_YORK": "ET",     "EASTERN TIME": "ET",  EASTERN: "ET",
    AKST: "AKT", AKDT: "AKT", "AMERICA/ANCHORAGE": "AKT", "ALASKA TIME": "AKT",  ALASKA: "AKT",
    HDT: "HST", "PACIFIC/HONOLULU": "HST", "HAWAII TIME": "HST", HAWAII: "HST",
    GMT: "UTC", Z: "UTC", "ETC/UTC": "UTC", "COORDINATED UNIVERSAL TIME": "UTC",
};

/** Raw setting value → one of our keys, or null if it means nothing to us. */
export const normalizeZoneKey = (raw) => {
    const k = String(raw ?? "").trim().toUpperCase().replace(/\s+/g, " ");
    if (!k) return null;
    if (TIME_ZONES[k]) return k;
    return ZONE_ALIASES[k] || null;
};

/** True when the value maps to a real zone — lets callers keep looking. */
export const isKnownZoneKey = (raw) => normalizeZoneKey(raw) !== null;

/** Never returns undefined — unrecognized values fall back to UTC. */
export const resolveTimeZone = (key) =>
    TIME_ZONES[normalizeZoneKey(key) || DEFAULT_TIME_ZONE_KEY];

/** Wall-clock time right now in the given IANA zone, e.g. "9:42 AM". */
export const clockInZone = (iana) => {
    try {
        return new Intl.DateTimeFormat("en-US", {
            timeZone: iana, hour: "numeric", minute: "2-digit",
        }).format(new Date());
    } catch {
        return "";
    }
};

// ── Defaults for a brand-new schedule ──────────────────────────────
export const NEW_SCHEDULE = {
    scheduleId:     null,
    reportId:       "",
    scheduleName:   "",
    description:    "",
    recurrence:     RECURRENCE.DAILY,
    dayOfWeek:      1,            // Monday, used when recurrence = weekly
    dayOfMonth:     1,            // used when recurrence = monthly
    runDate:        "",           // used when recurrence = none
    generationTime: "06:00",
    recipients:     [],
    granularity:    GRANULARITY.DAY,
    timeRange:      TIME_RANGE.CURRENT_MONTH,
    includePdf:     true,
    includeCsv:     false,
    isEnabled:      true,
};

// ── Labels (lookup by stored value) ────────────────────────────────
export const RECURRENCE_LABEL = Object.fromEntries(
    RECURRENCE_OPTIONS.map(o => [o.value, o.label])
);
export const GRANULARITY_LABEL = Object.fromEntries(
    GRANULARITY_OPTIONS.map(o => [o.value, o.label])
);
export const TIME_RANGE_LABEL = Object.fromEntries(
    TIME_RANGE_OPTIONS.map(o => [o.value, o.label])
);

// ── Formatting helpers ─────────────────────────────────────────────
export function ordinal(n) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** "06:00" → "6:00 AM" */
export const formatClock = (hhmm) => {
    if (!hhmm) return "—";
    const [h, m] = String(hhmm).split(":").map(Number);
    if (Number.isNaN(h)) return hhmm;
    const suffix = h >= 12 ? "PM" : "AM";
    const h12    = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m || 0).padStart(2, "0")} ${suffix}`;
};

/** "2026-08-14" → "08/14/2026" (parsed as local, never UTC-shifted) */
export const formatDateOnly = (val) => {
    if (!val) return "—";
    const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return String(val);
    return `${m[2]}/${m[3]}/${m[1]}`;
};

/** Human summary of when a schedule fires, e.g. "Every Tuesday". */
export const describeRecurrence = (s = {}) => {
    switch (s.recurrence) {
        case RECURRENCE.NONE:
            return s.runDate ? `Once on ${formatDateOnly(s.runDate)}` : "Once";
        case RECURRENCE.WEEKLY:
            return `Every ${DAY_OF_WEEK_LABEL[s.dayOfWeek] || "week"}`;
        case RECURRENCE.MONTHLY:
            return s.dayOfMonth === LAST_DAY_OF_MONTH
                ? "Monthly, last day"
                : `Monthly on the ${ordinal(s.dayOfMonth || 1)}`;
        case RECURRENCE.DAILY:
        default:
            return "Every day";
    }
};

/** Full one-liner used in the dialog footer, e.g. "Every Tuesday at 6:00 AM CT". */
export const describeSchedule = (s = {}, tzKey) => {
    const tz = resolveTimeZone(tzKey);
    return `${describeRecurrence(s)} at ${formatClock(s.generationTime)} ${tz.key}`;
};

// ── Copy ───────────────────────────────────────────────────────────
export const REPORT_STRINGS = {
    PAGE_TITLE:        "Report configuration",
    ADD_SCHEDULE:      "Add schedule",
    EDIT_SCHEDULE:     "Edit schedule",
    NO_SCHEDULES:      "No report schedules yet. Add one to start delivering reports.",
    LOAD_FAILED:       "Couldn't load report schedules. Try again.",
    SAVE_FAILED:       "Couldn't save the schedule. Check the fields and try again.",
    DELETE_FAILED:     "Couldn't delete the schedule. Try again.",
    RUN_FAILED:        "Couldn't queue the run. Try again.",
    RUN_QUEUED:        "Run queued — recipients will get it shortly.",
    DELETE_CONFIRM:    "Delete this schedule? Recipients stop receiving this report.",
    REQUIRE_REPORT:    "Pick a report.",
    REQUIRE_NAME:      "Name this schedule so it's recognizable in the list.",
    REQUIRE_RECIPIENT: "Add at least one recipient.",
    REQUIRE_OUTPUT:    "Pick at least one output — PDF, CSV, or both.",
    REQUIRE_TIME:      "Pick a time for the report to run.",
    REQUIRE_RUN_DATE:  "Pick the date this one-time report should run.",
    PAST_RUN_DATE:     "Pick today or a future date.",
    INVALID_EMAIL:     "That doesn't look like an email address.",
    DUPLICATE_EMAIL:   "That address is already on the list.",
    TZ_SOURCE:         "Run times use the time zone set in Configuration → Time Settings.",
    ONCE_NOTE:         "A one-time schedule pauses itself after it sends.",
};

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const ROWS_PER_PAGE = 25;

// Field limits — mirror the NVARCHAR widths on ReportSchedule.
export const MAX_SCHEDULE_NAME = 100;
export const MAX_DESCRIPTION   = 400;